import React, { useState, useEffect } from 'react';
import { dashboardAPI, costReportAPI, exportAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { fmt, symbolFor } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Download, 
  BarChart3, 
  PieChart,
  TrendingUp,
  Users,
  FileText,
  PoundSterling,
  FileSpreadsheet,
  CalendarIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import { format, subDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { cn } from '../lib/utils';

const COLORS = ['#1ABC9C', '#FFC04D', '#ef4444', '#6366f1', '#8b5cf6'];

// Date formatter (DD/MM/YYYY)
const formatDateUK = (dateStr) => {
  if (!dateStr || dateStr.length < 10) return dateStr;
  const parts = dateStr.substring(0, 10).split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

export default function Reports() {
  const { user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const money = (v, c) => fmt(v, c || orgCurrency);
  const [coverage, setCoverage] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [finance, setFinance] = useState(null);
  const [costReport, setCostReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [costLoading, setCostLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState(new Date());
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const [coverageRes, complianceRes, financeRes] = await Promise.all([
        dashboardAPI.getShiftCoverage(30),
        dashboardAPI.getComplianceSummary(),
        dashboardAPI.getFinanceSummary(),
      ]);
      
      setCoverage(coverageRes.data);
      setCompliance(complianceRes.data);
      setFinance(financeRes.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostReport = async () => {
    setCostLoading(true);
    try {
      const response = await costReportAPI.get({
        date_from: format(dateFrom, 'yyyy-MM-dd'),
        date_to: format(dateTo, 'yyyy-MM-dd'),
      });
      setCostReport(response.data);
    } catch (error) {
      toast.error('Failed to load cost report');
    } finally {
      setCostLoading(false);
    }
  };

  const handleCSVExport = async (exportType) => {
    setExportLoading(true);
    try {
      // Get the CSV URL
      const baseUrl = exportAPI.getCSVUrl(exportType, format(dateFrom, 'yyyy-MM-dd'), format(dateTo, 'yyyy-MM-dd'));
      
      // Fetch with credentials (httpOnly cookie)
      const response = await fetch(baseUrl, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportType}_export_${format(new Date(), 'dd-MM-yyyy')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`CSV exported successfully`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const complianceData = compliance ? [
    { name: 'Compliant', value: compliance.compliant, color: '#1ABC9C' },
    { name: 'Expiring Soon', value: compliance.expiring_soon, color: '#FFC04D' },
    { name: 'Expired', value: compliance.expired, color: '#ef4444' },
    { name: 'Pending', value: compliance.pending, color: '#6366f1' },
  ].filter(item => item.value > 0) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-container">
      {/* Date Range Filter */}
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">From Date</Label>
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[180px] justify-start text-left font-normal bg-[#1A1A1A] border-white/10 hover:bg-[#2B2B2B]",
                      !dateFrom && "text-muted-foreground"
                    )}
                    data-testid="date-from-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#A1A1AA]" />
                    {dateFrom ? format(dateFrom, "dd/MM/yyyy") : <span className="text-[#A1A1AA]">Select date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#2B2B2B] border-white/10" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={(date) => {
                      setDateFrom(date);
                      setFromOpen(false);
                    }}
                    locale={enGB}
                    initialFocus
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">To Date</Label>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[180px] justify-start text-left font-normal bg-[#1A1A1A] border-white/10 hover:bg-[#2B2B2B]",
                      !dateTo && "text-muted-foreground"
                    )}
                    data-testid="date-to-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#A1A1AA]" />
                    {dateTo ? format(dateTo, "dd/MM/yyyy") : <span className="text-[#A1A1AA]">Select date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#2B2B2B] border-white/10" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={(date) => {
                      setDateTo(date);
                      setToOpen(false);
                    }}
                    locale={enGB}
                    initialFocus
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button onClick={fetchCostReport} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" disabled={costLoading}>
              {costLoading ? 'Loading...' : 'Generate Cost Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="cost">
        <TabsList className="bg-[#2B2B2B]">
          <TabsTrigger value="cost" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white">
            <PoundSterling className="w-4 h-4 mr-2" />
            Cost Report
          </TabsTrigger>
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="compliance" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white">
            <PieChart className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="exports" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            CSV Exports
          </TabsTrigger>
        </TabsList>

        {/* Cost Report Tab */}
        <TabsContent value="cost" className="mt-6 space-y-6">
          {costReport && costReport.summary ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-[#2B2B2B] border-white/5">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1ABC9C]/20 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#1ABC9C]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Total Shifts</p>
                        <p className="text-2xl font-bold text-[#F2F2F2]">{costReport.summary.total_shifts || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#2B2B2B] border-white/5">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FFC04D]/20 flex items-center justify-center">
                        <PoundSterling className="w-5 h-5 text-[#FFC04D]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Total Payable</p>
                        <p className="text-2xl font-bold text-[#FFC04D]">{money(costReport.summary.total_payable)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#2B2B2B] border-white/5">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Total Chargeable</p>
                        <p className="text-2xl font-bold text-indigo-400">{money(costReport.summary.total_chargeable)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#2B2B2B] border-white/5">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1ABC9C]/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-[#1ABC9C]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#A1A1AA]">Total Profit</p>
                        <p className="text-2xl font-bold text-[#1ABC9C]">{money(costReport.summary.total_profit)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cost by Medic */}
              <Card className="bg-[#2B2B2B] border-white/5">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-[#F2F2F2]">Cost by Medic</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCSVExport('cost-report')}
                    disabled={exportLoading}
                    className="border-[#1ABC9C] text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                        <TableHead className="text-[#A1A1AA] text-right">Shifts</TableHead>
                        <TableHead className="text-[#A1A1AA] text-right">Payable</TableHead>
                        <TableHead className="text-[#A1A1AA] text-right">Chargeable</TableHead>
                        <TableHead className="text-[#A1A1AA] text-right">Profit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!costReport.by_medic || costReport.by_medic.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-[#A1A1AA] py-8">
                            No data for selected period
                          </TableCell>
                        </TableRow>
                      ) : (
                        costReport.by_medic.map((medic) => (
                          <TableRow key={medic.medic_id} className="border-white/5 hover:bg-white/5">
                            <TableCell className="text-[#F2F2F2] font-medium">{medic.medic_name}</TableCell>
                            <TableCell className="text-[#A1A1AA] text-right">{medic.shift_count}</TableCell>
                            <TableCell className="text-[#FFC04D] text-right">{money(medic.total_payable)}</TableCell>
                            <TableCell className="text-indigo-400 text-right">{money(medic.total_chargeable)}</TableCell>
                            <TableCell className="text-[#1ABC9C] text-right font-medium">{money(medic.profit)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Shift Details */}
              <Card className="bg-[#2B2B2B] border-white/5">
                <CardHeader>
                  <CardTitle className="text-[#F2F2F2]">Shift Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/5 hover:bg-transparent">
                          <TableHead className="text-[#A1A1AA]">Date</TableHead>
                          <TableHead className="text-[#A1A1AA]">Medic</TableHead>
                          <TableHead className="text-[#A1A1AA]">Location</TableHead>
                          <TableHead className="text-[#A1A1AA] text-right">Payable</TableHead>
                          <TableHead className="text-[#A1A1AA] text-right">Chargeable</TableHead>
                          <TableHead className="text-[#A1A1AA] text-right">Profit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costReport.shifts && costReport.shifts.slice(0, 50).map((shift) => (
                          <TableRow key={shift.shift_id} className="border-white/5 hover:bg-white/5">
                            <TableCell className="text-[#A1A1AA]">{formatDateUK(shift.date)}</TableCell>
                            <TableCell className="text-[#F2F2F2]">{shift.medic_name}</TableCell>
                            <TableCell className="text-[#A1A1AA]">{shift.location}</TableCell>
                            <TableCell className="text-[#FFC04D] text-right">{money(shift.rate_payable, shift.currency)}</TableCell>
                            <TableCell className="text-indigo-400 text-right">{money(shift.rate_chargeable, shift.currency)}</TableCell>
                            <TableCell className="text-[#1ABC9C] text-right">{money(shift.profit, shift.currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="py-12 text-center">
                <PoundSterling className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Select a date range and click &quot;Generate Cost Report&quot; to view cost data</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1ABC9C]/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#1ABC9C]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#A1A1AA]">Total Revenue</p>
                    <p className="text-2xl font-bold text-[#1ABC9C]">{money(finance?.total_revenue || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FFC04D]/20 flex items-center justify-center">
                    <PoundSterling className="w-5 h-5 text-[#FFC04D]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#A1A1AA]">Total Costs</p>
                    <p className="text-2xl font-bold text-[#FFC04D]">{money(finance?.total_costs || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-sm text-[#A1A1AA]">Net Profit</p>
                    <p className="text-2xl font-bold text-indigo-400">{money(finance?.net_profit || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2]">30-Day Shift Coverage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                  <BarChart data={coverage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#A1A1AA' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#2B2B2B', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#F2F2F2' }}
                    />
                    <Bar dataKey="assigned" fill="#1ABC9C" name="Assigned" />
                    <Bar dataKey="unassigned" fill="#FFC04D" name="Unassigned" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2]">Compliance Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={complianceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {complianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#2B2B2B', border: '1px solid #333', borderRadius: '8px' }}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2]">Compliance Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {complianceData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-[#F2F2F2]">{item.name}</span>
                    </div>
                    <span className="text-lg font-bold" style={{ color: item.color }}>{item.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* CSV Exports Tab */}
        <TabsContent value="exports" className="mt-6">
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardHeader>
              <CardTitle className="text-[#F2F2F2]">Export Data as CSV</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#A1A1AA] mb-6">
                Export data in CSV format with dates in DD/MM/YYYY format and currency in {orgCurrency} ({symbolFor(orgCurrency)}).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Button
                  onClick={() => handleCSVExport('shifts')}
                  disabled={exportLoading}
                  className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 h-auto py-4 flex-col"
                  data-testid="export-shifts-btn"
                >
                  <FileSpreadsheet className="w-6 h-6 mb-2" />
                  <span>Export Shifts</span>
                </Button>
                <Button
                  onClick={() => handleCSVExport('medics')}
                  disabled={exportLoading}
                  className="bg-[#FFC04D] hover:bg-[#FFC04D]/90 text-black h-auto py-4 flex-col"
                  data-testid="export-medics-btn"
                >
                  <Users className="w-6 h-6 mb-2" />
                  <span>Export Medics</span>
                </Button>
                <Button
                  onClick={() => handleCSVExport('contracts')}
                  disabled={exportLoading}
                  className="bg-indigo-500 hover:bg-indigo-600 h-auto py-4 flex-col"
                  data-testid="export-contracts-btn"
                >
                  <FileText className="w-6 h-6 mb-2" />
                  <span>Export Contracts</span>
                </Button>
                <Button
                  onClick={() => handleCSVExport('cost-report')}
                  disabled={exportLoading}
                  className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 h-auto py-4 flex-col"
                  data-testid="export-cost-report-btn"
                >
                  <PoundSterling className="w-6 h-6 mb-2" />
                  <span>Export Cost Report</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
